/**
 * Script de verificación del mapeo de propiedades
 * 
 * Este script comprueba qué propiedades del mapeo existen en Supabase
 * y cuáles faltan, para asegurar que el importador funcionará correctamente.
 */

import { supabase } from '../config/supabase';
import { PROPERTY_MAPPINGS, getMappingStats } from '../config/propertyMapping';

export interface VerificationResult {
  total: number;
  found: number;
  missing: number;
  foundProperties: Array<{
    propertyName: string;
    ownerName: string;
    id: string;
  }>;
  missingProperties: Array<{
    propertyName: string;
    ownerName: string;
  }>;
  extraProperties: Array<{
    id: string;
    name: string;
  }>;
}

/**
 * Verifica el mapeo de propiedades contra la base de datos
 */
export async function verifyPropertyMapping(): Promise<VerificationResult> {
  console.log('🔍 Verificando mapeo de propiedades...\n');

  // Obtener todas las propiedades de Supabase
  const { data: dbProperties, error } = await supabase
    .from('properties')
    .select('id, name')
    .order('name');

  if (error) {
    console.error('❌ Error obteniendo propiedades de Supabase:', error);
    throw error;
  }

  if (!dbProperties || dbProperties.length === 0) {
    console.warn('⚠️ No se encontraron propiedades en la base de datos');
    return {
      total: PROPERTY_MAPPINGS.length,
      found: 0,
      missing: PROPERTY_MAPPINGS.length,
      foundProperties: [],
      missingProperties: PROPERTY_MAPPINGS.map(m => ({
        propertyName: m.propertyName,
        ownerName: m.ownerName,
      })),
      extraProperties: [],
    };
  }

  console.log(`📊 Propiedades en Supabase: ${dbProperties.length}`);
  console.log(`📊 Propiedades en mapeo: ${PROPERTY_MAPPINGS.length}\n`);

  // Verificar cada propiedad del mapeo
  const foundProperties: VerificationResult['foundProperties'] = [];
  const missingProperties: VerificationResult['missingProperties'] = [];

  for (const mapping of PROPERTY_MAPPINGS) {
    const dbProperty = dbProperties.find(
      p => p.name.toLowerCase() === mapping.propertyName.toLowerCase()
    );

    if (dbProperty) {
      foundProperties.push({
        propertyName: mapping.propertyName,
        ownerName: mapping.ownerName,
        id: dbProperty.id,
      });
    } else {
      missingProperties.push({
        propertyName: mapping.propertyName,
        ownerName: mapping.ownerName,
      });
    }
  }

  // Encontrar propiedades en BD que no están en el mapeo
  const mappedPropertyNames = PROPERTY_MAPPINGS.map(m => m.propertyName.toLowerCase());
  const extraProperties = dbProperties.filter(
    p => !mappedPropertyNames.includes(p.name.toLowerCase())
  );

  // Mostrar resultados
  console.log('✅ PROPIEDADES ENCONTRADAS:', foundProperties.length);
  if (foundProperties.length > 0) {
    foundProperties.forEach(p => {
      console.log(`   ✓ ${p.propertyName} (${p.ownerName})`);
    });
  }

  console.log('\n❌ PROPIEDADES FALTANTES:', missingProperties.length);
  if (missingProperties.length > 0) {
    missingProperties.forEach(p => {
      console.log(`   ✗ ${p.propertyName} (${p.ownerName})`);
    });
  }

  console.log('\n⚠️ PROPIEDADES EN BD SIN MAPEO:', extraProperties.length);
  if (extraProperties.length > 0) {
    extraProperties.forEach(p => {
      console.log(`   ? ${p.name}`);
    });
  }

  console.log('\n' + '='.repeat(60));
  console.log(`RESUMEN:`);
  console.log(`  Total en mapeo: ${PROPERTY_MAPPINGS.length}`);
  console.log(`  Encontradas: ${foundProperties.length} ✅`);
  console.log(`  Faltantes: ${missingProperties.length} ${missingProperties.length > 0 ? '❌' : '✅'}`);
  console.log(`  Extra en BD: ${extraProperties.length} ${extraProperties.length > 0 ? '⚠️' : '✅'}`);
  console.log('='.repeat(60));

  if (missingProperties.length === 0) {
    console.log('\n🎉 ¡Perfecto! Todas las propiedades del mapeo existen en Supabase');
  } else {
    console.log('\n⚠️ ATENCIÓN: Hay propiedades faltantes en Supabase');
    console.log('   Debes crear estas propiedades antes de importar datos');
  }

  return {
    total: PROPERTY_MAPPINGS.length,
    found: foundProperties.length,
    missing: missingProperties.length,
    foundProperties,
    missingProperties,
    extraProperties,
  };
}

/**
 * Genera un script SQL para crear las propiedades faltantes
 */
export function generateCreatePropertiesSQL(missingProperties: VerificationResult['missingProperties']): string {
  if (missingProperties.length === 0) {
    return '-- No hay propiedades faltantes';
  }

  let sql = `-- Script para crear propiedades faltantes
-- Generado automáticamente el ${new Date().toLocaleString()}
-- Total de propiedades a crear: ${missingProperties.length}

`;

  missingProperties.forEach((prop, index) => {
    sql += `-- ${index + 1}. ${prop.propertyName} (Propietario: ${prop.ownerName})
INSERT INTO properties (name, location, stats)
VALUES (
  '${prop.propertyName}',
  '${prop.ownerName}',
  '{
    "revenue": 0,
    "cleaningCosts": 0,
    "inmohostCosts": 0,
    "profit": 0,
    "occupancyRate": 0,
    "reservations": 0,
    "averageNightPrice": 0,
    "averageStayDuration": 0
  }'
)
ON CONFLICT (name) DO NOTHING;

`;
  });

  return sql;
}

/**
 * Función helper para usar en la consola del navegador
 */
export async function checkMapping() {
  try {
    const result = await verifyPropertyMapping();
    
    if (result.missing > 0) {
      console.log('\n📝 Script SQL para crear propiedades faltantes:');
      console.log(generateCreatePropertiesSQL(result.missingProperties));
    }
    
    return result;
  } catch (error) {
    console.error('Error verificando mapeo:', error);
    throw error;
  }
}

// Exportar para uso en consola
if (typeof window !== 'undefined') {
  (window as any).checkPropertyMapping = checkMapping;
  console.log('💡 Usa window.checkPropertyMapping() para verificar el mapeo');
}
